import { Box, Button, Drawer, Typography, useMediaQuery } from "@mui/material";
import { type ResponsiveStyleValue } from "@mui/system";
import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

interface AdminDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  icon: ReactNode;
  children: ReactNode;
  width?: ResponsiveStyleValue<number | string>;
  closeLabel?: string;
}

/** Shared admin drawer below the Global Header with section styling and an independently scrolling body. */
export const AdminDrawer = ({
  open,
  onClose,
  title,
  icon,
  children,
  width = { xs: "100%", sm: 440 },
  closeLabel,
}: AdminDrawerProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  useEffect(() => {
    if (open) {
      openerRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }
  }, [open]);

  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open={open}
      transitionDuration={prefersReducedMotion ? 0 : undefined}
      slotProps={{
        transition: {
          onEntered: () => closeButtonRef.current?.focus(),
          onExited: () => {
            if (openerRef.current?.isConnected) openerRef.current.focus();
          },
        },
        paper: {
          role: "dialog",
          "aria-labelledby": titleId,
          onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "Escape") {
              event.stopPropagation();
              onClose();
            }
          },
          sx: {
            width,
            maxWidth: "100%",
            boxSizing: "border-box",
            top: (theme) => theme.spacing(8),
            height: (theme) => `calc(100% - ${theme.spacing(8)})`,
            p: 2,
            overflow: "hidden",
            zIndex: (theme) => theme.zIndex.drawer,
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
          flexShrink: 0,
        }}
      >
        <Typography
          id={titleId}
          variant="h6"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "primary.main",
            fontSize: { xs: "1.1rem", sm: "1.15rem" },
          }}
        >
          {icon}
          {title}
        </Typography>
        <Button
          ref={closeButtonRef}
          variant="text"
          size="small"
          onClick={onClose}
          sx={{ flexShrink: 0 }}
        >
          {closeLabel ?? t("common.close")}
        </Button>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>{children}</Box>
    </Drawer>
  );
};
